"use client";

import { useNavigation } from "@/hooks/useNavigation";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { cloneElement, memo, useCallback } from "react";

export const RouterLink = memo(
  /**
   * @typedef {object} Props
   * @prop {string} [href]
   * @prop {boolean} [asChild]
   * @prop {import("react").ReactElement} children
   */
  /**
   * @param {Props & Omit<import("next/link").LinkProps, "href">} props
   */
  function RouterLink(props) {
    const {
      asChild,
      children,
      href,
      onClick: _onClick,
      prefetch = false,
      ...otherProps
    } = props;

    const { startLoading } = useNavigation();

    const currentPathname = usePathname();

    const onClick = useCallback(
      /** @type {import("react").MouseEventHandler<HTMLAnchorElement>} */
      (e) => {
        _onClick?.(e);
        // Si le lien est différent de la page actuelle et que l'utilisateur n'a pas cliqué avec Ctrl (ou ⌘) pour ouvrir une nouvelle page, on active la barre de chargement
        if (currentPathname !== href && !e.ctrlKey && !e.metaKey) {
          startLoading();
        }
      },
      [_onClick, currentPathname, href, startLoading],
    );

    if (href === undefined) {
      return props.children;
    }

    return (
      <NextLink
        {...otherProps}
        href={href}
        legacyBehavior={asChild}
        passHref={asChild}
        prefetch={prefetch}
        onClick={asChild ? undefined : onClick}
      >
        {asChild
          ? cloneElement(children, {
              onClick:
                /** @param {any} event */
                (event) => {
                  onClick(event);
                  children.props.onClick?.(event);
                },
            })
          : children}
      </NextLink>
    );
  },
);
